import axios from "axios";
import { API } from "../config";

// Isolated axios instance for Ads Creator — carries cookies so JWT sessions
// from the main TopRankPulse app are transparently recognized.
export const adsApi = axios.create({
  baseURL: `${API}/ads-creator`,
  withCredentials: true,
});

// Inject Bearer token from localStorage if present (partner/iframe embeds).
adsApi.interceptors.request.use((cfg) => {
  const tok = typeof window !== "undefined" ? localStorage.getItem("access_token") : null;
  if (tok) cfg.headers.Authorization = `Bearer ${tok}`;
  return cfg;
});

export const adsApiCall = {
  meta: () =>
    Promise.all([
      adsApi.get("/meta/formats"),
      adsApi.get("/meta/categories"),
      adsApi.get("/meta/pricing"),
    ]).then(([f, c, p]) => ({
      formats: f.data.formats,
      categories: c.data.categories,
      packages: p.data.packages,
    })),

  identity: () => adsApi.get("/meta/identity").then((r) => r.data),
  credits: () => adsApi.get("/credits").then((r) => r.data),
  publicStats: () => adsApi.get("/stats/public").then((r) => r.data),

  listTemplates: (params = {}) =>
    adsApi.get("/templates", { params }).then((r) => r.data),
  useTemplate: (id) => adsApi.post(`/templates/${id}/use`).then((r) => r.data),

  listProjects: () => adsApi.get("/projects").then((r) => r.data),
  getProject: (id) => adsApi.get(`/projects/${id}`).then((r) => r.data),
  createProject: (body) => adsApi.post("/projects", body).then((r) => r.data),
  updateProject: (id, body) => adsApi.put(`/projects/${id}`, body).then((r) => r.data),
  duplicateProject: (id) => adsApi.post(`/projects/${id}/duplicate`).then((r) => r.data),
  deleteProject: (id) => adsApi.delete(`/projects/${id}`).then((r) => r.data),

  aiMagic: (body) => adsApi.post("/ai/magic", body).then((r) => r.data),
  aiReelDirector: (body) => adsApi.post("/ai/reel-director", body).then((r) => r.data),
  aiMultiSceneDirector: (body, sceneCount = 4) =>
    adsApi.post(`/ai/multi-scene-director?scene_count=${sceneCount}`, body).then((r) => r.data),
  aiPhotoDirector: (body) => adsApi.post("/ai/photo-director", body).then((r) => r.data),
  aiPhotoReel: (body) => adsApi.post("/ai/photo-reel", body).then((r) => r.data),
  aiPhotoPack: (body) => adsApi.post("/ai/photo-pack", body).then((r) => r.data),
  aiEpicSceneReel: (body) => adsApi.post("/ai/epic-scene-reel", body).then((r) => r.data),
  epicScenesCatalog: () => adsApi.get("/ai/epic-scenes/catalog").then((r) => r.data),
  listAssets: (limit = 50, includeData = false) =>
    adsApi.get(`/assets?limit=${limit}&include_data=${includeData ? 1 : 0}`).then((r) => r.data),
  uploadAsset: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return adsApi.post("/assets/upload", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then((r) => r.data);
  },
  getAsset: (id) => adsApi.get(`/assets/${id}`).then((r) => r.data),
  aiBatchDirector: (body) => adsApi.post("/ai/batch-director", body).then((r) => r.data),
  aiInstantCampaign: (body) => adsApi.post("/ai/instant-campaign", body).then((r) => r.data),
  remixSeed: (id) => adsApi.get(`/campaigns/${id}/remix-seed`).then((r) => r.data),
  listCampaigns: () => adsApi.get("/campaigns/mine").then((r) => r.data),
  shareCampaign: (id) => adsApi.post(`/campaigns/${id}/share`).then((r) => r.data),
  unshareCampaign: (id) => adsApi.post(`/campaigns/${id}/unshare`).then((r) => r.data),
  publicCampaign: (id) => adsApi.get(`/campaigns/${id}/public`).then((r) => r.data),
  aiCopy: (body) => adsApi.post("/ai/copy-suggest", body).then((r) => r.data),
  aiImage: (body) => adsApi.post("/ai/generate-image", body).then((r) => r.data),
  smartResize: (projectId) => adsApi.post(`/ai/smart-resize/${projectId}`).then((r) => r.data),

  exportProject: (body) => adsApi.post("/export", body).then((r) => r.data),
  listExports: () => adsApi.get("/exports").then((r) => r.data),
  trackCampaign: (id, event, channel) => {
    try {
      let visitor_id = localStorage.getItem("trp_vid");
      if (!visitor_id) {
        visitor_id = Math.random().toString(36).slice(2) + Date.now().toString(36);
        localStorage.setItem("trp_vid", visitor_id);
      }
      return adsApi.post(`/campaigns/${id}/track`, { event, channel, visitor_id }).then((r) => r.data);
    } catch { return Promise.resolve({}); }
  },
  setCampaignSlug: (id, slug) => adsApi.put(`/campaigns/${id}/slug`, { slug }).then((r) => r.data),
  deleteCampaignSlug: (id) => adsApi.delete(`/campaigns/${id}/slug`).then((r) => r.data),
  slugAvailable: (slug) => adsApi.get(`/campaigns/slug-available/${encodeURIComponent(slug)}`).then((r) => r.data),
  setLeadsEnabled: (id, enabled) => adsApi.put(`/campaigns/${id}/leads-toggle`, { enabled }).then((r) => r.data),
  captureLead: (id, payload) => adsApi.post(`/campaigns/${id}/leads`, payload).then((r) => r.data),
  listLeads: (id) => adsApi.get(`/campaigns/${id}/leads`).then((r) => r.data),
  creditsLedger: (limit = 100) => adsApi.get(`/credits/ledger?limit=${limit}`).then((r) => r.data),
  digestStatus: () => adsApi.get("/digest/status").then((r) => r.data),
  digestSubscribe: () => adsApi.post("/digest/subscribe").then((r) => r.data),
  digestUnsubscribe: () => adsApi.post("/digest/unsubscribe").then((r) => r.data),
  statementStatus: () => adsApi.get("/statement/status").then((r) => r.data),
  statementSubscribe: () => adsApi.post("/statement/subscribe").then((r) => r.data),
  statementUnsubscribe: () => adsApi.post("/statement/unsubscribe").then((r) => r.data),
  achievementsMine: () => adsApi.get("/achievements/mine").then((r) => r.data),
  publicAchievement: (hash, id) => adsApi.get(`/achievements/public/hash/${hash}/${id}`).then((r) => r.data),
  campaignStats: (id) => adsApi.get(`/campaigns/${id}/stats`).then((r) => r.data),
  redeemCoupon: (code) => adsApi.post("/credits/coupons/redeem", { code }).then((r) => r.data),
  myReferralCoupon: () => adsApi.get("/credits/coupons/my-referral").then((r) => r.data),
  shareProjectWithTeam: (id, share) =>
    adsApi.post(`/projects/${id}/share-with-team`, { share }).then((r) => r.data),
  myTeam: () => adsApi.get("/team/me").then((r) => r.data),
};
